/**
 * Категория внутри раскрытого бренда (§4.3).
 *
 * Это не строка таблицы. Раньше категория рисовала весь набор колонок, и во
 * всех неприменимых стояло «·» на сером фоне — получалась простыня пустых
 * ячеек. Категория — самостоятельная сущность бренда: имя, тир в активном
 * канале и отвязка по наведению. Остальная ширина строки остаётся пустой,
 * чтобы колонки таблицы к категориям не относились.
 *
 * Отвязка неразрушающая (П6): она живёт в черновике, строка помечается, и до
 * подтверждения её можно откатить — поэтому диалога подтверждения здесь нет.
 */
import { useState } from 'react'
import {
  IconClose, IconPlus, IconButton, Select, TierBadge, Tooltip,
} from '../../../components/index.js'
import { categoryCell } from '../../../lib/draft-overlay.js'
import { useApp } from '../../../store/app-store.js'
import { enumOptions } from '../card/FieldsBlock.js'
import { CategoryPicker } from '../card/CategoriesBlock.js'
import { cardTexts } from '../card/texts.card.js'
import { tableTexts as t } from '../texts.table.js'
import type { TableLayout } from './BrandRow.js'
import type { TableRow } from './rows.js'
import type { ValueContext } from './values.js'
import './CategoryRow.css'

/**
 * Отступ имени категории: ровно столько, чтобы чип тира встал под колонкой
 * «Тир». Иначе он оказывался под заголовком «Видимость» — глаз читает его как
 * значение не того столбца.
 */
function nameWidth(layout: TableLayout): number | null {
  const tierLeft = layout.leftOffset.get('tier')
  if (tierLeft === undefined) return null
  /* Внутри плашки есть свой левый отступ и ёлочка: вычитаем их. */
  return Math.max(80, tierLeft - 24 - 14)
}

/** Ширина закреплённой слева зоны: категории живут внутри неё и не уезжают. */
function pinnedWidth(layout: TableLayout): number {
  let width = 0
  for (const column of layout.columns) {
    const left = layout.leftOffset.get(column.code)
    if (left === undefined) continue
    width = Math.max(width, left + column.width)
  }
  return width > 0 ? width : 420
}

export interface CategoryRowProps {
  row: Extract<TableRow, { kind: 'category' }>
  rowIndex: number
  top: number
  height: number
  layout: TableLayout
  ctx: ValueContext
  /** Правка запрещена целиком (роль только читает) либо только у пустых. */
  readOnly: boolean
}

export function CategoryRow({ row, rowIndex, top, height, layout, ctx, readOnly }: CategoryRowProps) {
  const overlay = useApp((state) => state.overlay)
  const user = useApp((state) => state.bootstrap?.user ?? null)
  const editCategoryChannel = useApp((state) => state.editCategoryChannel)
  const detachCategory = useApp((state) => state.detachCategory)

  const detached = overlay.detached.has(row.category.id)
  const isDraft = row.category.isDraft || overlay.draftCategories.has(row.category.id)
  const cell = categoryCell<string>(overlay, row.category, ctx.channel, 'tier')
  const mayDetach = Boolean(user?.permissions.includes('categories.detach')) && !readOnly

  const classes = ['cat-item']
  if (detached) classes.push('cat-item--detached')
  else if (isDraft || cell.isDraft) classes.push('cat-item--draft')

  return (
    <div
      className="cat-row"
      role="row"
      aria-rowindex={rowIndex + 1}
      style={{ top: `${top}px`, height: `${height}px`, width: `${layout.totalWidth}px` }}
    >
      <div className={classes.join(' ')} style={{ width: `${pinnedWidth(layout)}px` }} role="gridcell">
        {/* Ветка «└» достаётся строке «Добавить» — она и есть последняя. */}
        <span className="cat-item__tree" aria-hidden="true">├</span>
        <span
          className="cat-item__name t-cat t-truncate"
          style={nameWidth(layout) === null ? undefined : { width: `${nameWidth(layout)}px`, flex: 'none' }}
        >
          {row.category.name}
        </span>

        <CategoryTier
          value={typeof cell.value === 'string' ? cell.value : null}
          label={t.edit.cellLabel(t.column.tierFull, `${row.brand.name} · ${row.category.name}`)}
          disabled={readOnly || detached}
          onChange={(tier) => void editCategoryChannel(row.brand.id, row.category.id, ctx.channel, { tier })}
        />

        {detached ? (
          <span className="cat-item__mark t-meta">{cardTexts.categories.detachedMark}</span>
        ) : (
          <span className="cat-item__detach">
            <Tooltip content={cardTexts.categories.detach}>
              <IconButton
                label={`${cardTexts.categories.detach}: ${row.category.name}`}
                icon={<IconClose size={16} />}
                variant="ghost"
                size="s"
                disabled={!mayDetach}
                onClick={() => void detachCategory(row.brand.id, row.category.id)}
              />
            </Tooltip>
          </span>
        )}
      </div>
    </div>
  )
}

/** Тир категории: чип, который по клику превращается в выбор из справочника. */
function CategoryTier({ value, label, disabled, onChange }: {
  value: string | null
  label: string
  disabled: boolean
  onChange: (value: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const enums = useApp((state) => state.bootstrap?.enums)
  const options = enumOptions(enums, 'tier').map((option) => ({
    ...option,
    badge: <TierBadge tier={option.value} />,
  }))

  return (
    <Select
      className="cat-item__tier"
      value={value}
      options={options}
      onChange={onChange}
      open={open}
      onOpenChange={setOpen}
      label={label}
      size="cell"
      variant="plain"
      emptyLabel={cardTexts.value.tierEmpty}
      clearLabel={cardTexts.value.clear}
      disabled={disabled}
    />
  )
}

/**
 * Хвост списка категорий: «+ Добавить категорию». Открывает тот же справочник,
 * что и карточка, — свободного ввода нет нигде (критерий приёмки №6).
 */
export function AddCategoryRow({ row, rowIndex, top, height, layout }: {
  row: Extract<TableRow, { kind: 'add-category' | 'no-categories' }>
  rowIndex: number
  top: number
  height: number
  layout: TableLayout
}) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const user = useApp((state) => state.bootstrap?.user ?? null)
  const attachCategory = useApp((state) => state.attachCategory)

  const mayAttach = Boolean(user?.permissions.includes('categories.attach'))
  const attachedIds = new Set(row.brand.categories.map((item) => item.categoryId))
  const empty = row.kind === 'no-categories'

  return (
    <div
      className="cat-row"
      role="row"
      aria-rowindex={rowIndex + 1}
      style={{ top: `${top}px`, height: `${height}px`, width: `${layout.totalWidth}px` }}
    >
      <div className="cat-item cat-item--add" style={{ width: `${pinnedWidth(layout)}px` }} role="gridcell">
        <span className="cat-item__tree" aria-hidden="true">└</span>
        {empty ? <span className="cat-item__empty t-meta">{t.row.noCategories}</span> : null}
        <button
          ref={setAnchor}
          type="button"
          className="cat-item__add"
          disabled={!mayAttach}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((previous) => !previous)}
        >
          <IconPlus size={16} />
          {t.row.addCategory}
        </button>
      </div>

      <CategoryPicker
        open={open}
        anchor={anchor}
        onClose={() => setOpen(false)}
        attachedIds={attachedIds}
        onPick={(category) => {
          setOpen(false)
          void attachCategory(row.brand.id, category.id)
        }}
        onRequest={() => setOpen(false)}
      />
    </div>
  )
}
